import {
  AvatarFallback,
  CustomSidebarMenuButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { Avatar } from '@radix-ui/react-avatar';
import {
  PiArrowSquareOut,
  PiCaretUpDownThin,
  PiGear,
  PiQuestion,
  PiSignOut,
  PiTag,
  PiUsersThreeThin,
} from 'react-icons/pi';
import React from 'react';
import { useMapViewport } from '../../../modules/Settings/MapViewportContext';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../../routes';
import { useAuth } from '@ais-ui/auth';
import { useProfile } from '@ais-ui/profile';
import { useReleaseNotesContext } from '../../../modules/ReleaseNotes/ReleaseNotesProvider';
import { useVersion } from '../useVersion';
import { BugReportPopover } from './BugReport';
import { AsyncJobs } from './AsyncJobs';
import { useSentryStatus } from '../../../configuration/useSentry';
import {
  closeAllPopups,
  openPopupOnSecondaryScreen,
  PopupType,
} from '../../../modules/Popup';

interface UserDropdownProps {
  expanded: boolean;
  mobileMode?: boolean;
}

interface UserDropdownButtonProps {
  expanded: boolean;
  mobileMode?: boolean;
}

const UserDropDownButton = React.forwardRef<
  HTMLButtonElement,
  UserDropdownButtonProps
>(({ expanded, mobileMode = false, ...props }, ref) => {
  const { email, username, shortUsername } = useProfile();
  const { version } = useVersion();

  if (mobileMode) {
    return (
      <button
        ref={ref}
        {...props}
        id="mobile-user-avatar-button"
        className="size-12 flex items-center justify-center hover:bg-accent transition-colors rounded-lg"
      >
        <Avatar className="size-12 rounded-lg">
          <AvatarFallback className="rounded-lg bg-background shadow-md border">
            {shortUsername}
          </AvatarFallback>
        </Avatar>
      </button>
    );
  }

  if (expanded) {
    return (
      <CustomSidebarMenuButton
        ref={ref}
        {...props}
        size="lg"
        id={'user-menu-button'}
        className="text-sidebar-accent-foreground hover:bg-accent rounded-lg"
      >
        <Avatar className="size-8 rounded-lg relative">
          <AvatarFallback className="rounded-lg bg-blue-100 dark:bg-slate-700 text-primary dark:text-slate-200">
            {shortUsername}
          </AvatarFallback>
        </Avatar>
        <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
          <div className="flex items-center min-w-0 justify-between">
            <span className="truncate font-semibold mr-2 min-w-0">
              {username}
            </span>
            <span className="flex-shrink-0 text-xs whitespace-nowrap">
              v{version}
            </span>
          </div>
          <span className="truncate text-xs">{email}</span>
        </div>
        <PiCaretUpDownThin className="ml-auto h-6 w-6 flex-shrink-0" />
      </CustomSidebarMenuButton>
    );
  }
  return (
    <CustomSidebarMenuButton
      ref={ref}
      {...props}
      size="lg"
      className="flex justify-center text-sidebar-accent-foreground hover:bg-accent rounded-lg w-full"
    >
      <div className="relative size-8">
        <Avatar className="size-8 rounded-lg">
          <AvatarFallback className="rounded-lg bg-blue-100 dark:bg-slate-700 text-primary dark:text-slate-200">
            {shortUsername}
          </AvatarFallback>
        </Avatar>
      </div>
    </CustomSidebarMenuButton>
  );
});

const UserDropdownHeader = () => {
  const { email, username, shortUsername, groups } = useProfile();
  const { version } = useVersion();
  return (
    <DropdownMenuLabel className="p-0 font-normal">
      <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
        <Avatar className="size-8 rounded-lg">
          <AvatarFallback className="rounded-lg bg-blue-100 dark:bg-slate-700 text-primary dark:text-slate-200">
            {shortUsername}
          </AvatarFallback>
        </Avatar>
        <div className="grid flex-1 text-left text-sm leading-tight">
          <div className="flex justify-between space-x-4">
            <div className={'flex space-x-2'}>
              <span className="truncate font-semibold">{username}</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <PiUsersThreeThin className="size-4 mb-1"></PiUsersThreeThin>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="">
                      <h2 className="text-sm font-semibold">Groups</h2>
                      <Separator className="mt-1 mb-2"></Separator>
                      {groups.length > 0 ? (
                        <ul className="space-y-1">
                          {groups.map((group, index) => (
                            <li className={'text-sm'} key={index}>
                              {group}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className={'text-sm'}>Not member of any groups</p>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <span className="text-xs">v{version}</span>
          </div>
          <span className="truncate text-xs">{email}</span>
        </div>
      </div>
    </DropdownMenuLabel>
  );
};

export const UserDropdown = ({
  expanded,
  mobileMode = false,
}: UserDropdownProps) => {
  const { saveMapState } = useMapViewport();
  const { logout } = useAuth();
  const { setOpen: setOpenReleaseNotes } = useReleaseNotesContext();
  const isSentryInitialized = useSentryStatus();
  const navigate = useNavigate();

  const handleSettingsClick = () => {
    saveMapState();
    navigate(ROUTES.SETTINGS.GENERAL.$path());
  };

  const handleLogoutClick = () => {
    closeAllPopups();
    logout('');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <UserDropDownButton expanded={expanded} mobileMode={mobileMode} />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-64 rounded-lg"
        side={mobileMode ? 'bottom' : 'right'}
        align="end"
        sideOffset={4}
      >
        <UserDropdownHeader></UserDropdownHeader>

        <DropdownMenuSeparator />
        {!mobileMode && (
          <DropdownMenuGroup>
            <DropdownMenuItem
              id="sidebar-help-button"
              onClick={() => openPopupOnSecondaryScreen(PopupType.Help)}
              className="w-full h-full flex items-center group/btn hover:bg-secondary"
            >
              <PiQuestion className="h-4 w-4 mr-1" />
              <p className="flex-1">Help</p>
              <PiArrowSquareOut className="h-4 w-4 text-muted-foreground" />
            </DropdownMenuItem>
            <DropdownMenuItem
              id="sidebar-release-notes-button"
              onClick={() => setOpenReleaseNotes(true)}
              className="w-full h-full flex items-center group/btn space-x-1 hover:bg-secondary"
            >
              <PiTag className="size-4" />
              <p>Release notes</p>
            </DropdownMenuItem>
            <DropdownMenuItem
              id="sidebar-settings-button"
              onClick={handleSettingsClick}
              className="w-full h-full flex items-center group/btn space-x-1 hover:bg-secondary"
            >
              <PiGear className="size-4" />
              <p>Settings</p>
            </DropdownMenuItem>
            {/*{isSentryInitialized && <BugReportPopover></BugReportPopover>}*/}
            <BugReportPopover />
            <AsyncJobs></AsyncJobs>
          </DropdownMenuGroup>
        )}
        {!mobileMode && <DropdownMenuSeparator />}
        <DropdownMenuItem
          id="mobile-logout-button"
          onClick={handleLogoutClick}
          className="w-full h-full flex items-center group/btn space-x-1 hover:bg-secondary"
        >
          <PiSignOut className="size-4" />
          <p className="">Log out</p>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
